import { useEffect, useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import orderBy from 'lodash/orderBy';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  getActivePlanAssignmentsOptions,
  type Plan,
  type PlanAssignment,
} from '@cfa/pathway-fe-shared/xp';

import useAuth from '@/hooks/useAuth';
import { useCategoryLabels_v2 } from '@/hooks/useCategoryLabels';
import { useFlags } from '@/hooks/useFlags';
import { usePlansShouldShowTabs } from '@/hooks/usePlansShouldShowTabs';
import { useScrollMemory } from '@/hooks/useScrollMemory/useScrollMemory';
import {
  type AddSortFields,
  addSortFields,
  useSortOptions,
} from '@/hooks/useSortOptions';

import { itemByLanguage } from '@/utils/language';
import { isApiError } from '@/utils/request';
import { getTimeSpentOnPlan } from '@/utils/time';
import { withRoles } from '@/utils/withRoles';

import TeamMemberPlanCard, {
  type TeamMemberPlanCardProps,
} from '@/components/Cards/PlanCard/PlanCards/TeamMemberPlanCard';
import ClearFiltersHeader from '@/components/ClearFiltersHeader/ClearFiltersHeader';
import GenericError from '@/components/Error/GenericError';
import Filter, {
  FilterAndSortButtonProps,
  FilterCheckboxSectionProps,
  FilterDesktop,
  FilterMobile,
  FilterSearchBarProps,
} from '@/components/Filter';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import NoMessage from '@/components/NoMessage/NoMessage';
import SortFilterHeader from '@/components/SortFilterHeader/SortFilterHeader';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import Constants, { routes } from '@/constants';

import { sourceDataFamily, useDataView } from '@/atoms/data-view';
import { activeLicenseeLocationAtom } from '@/atoms/licensee-location';

const DATAVIEW_KEY = 'my-plans';

type MyPlanRow = AddSortFields<Plan> & {
  dueDate: string | null;
  isCompleted: string;
  assignments?: PlanAssignment[];
};

const toTeamMemberPlanCardProps = (
  plan: MyPlanRow & { id: string },
  assignment: PlanAssignment | undefined,
): TeamMemberPlanCardProps => ({
  link: routes.plans.plan({ planId: plan.id }),
  category: plan.category,
  name: plan.name,
  isComplete: assignment?.status === Constants.TRAINING_PLANS.COMPLETED,
  completedSteps: assignment?.stepsComplete,
  totalSteps: plan.totalSteps,
  timeSpentOnPlan: assignment ? getTimeSpentOnPlan({ ...assignment }) : null,
  dueDate: assignment?.dueDate,
  completionDate: assignment?.completionDate,
  lastCompletedDate:
    assignment?.latestCompletionReport?.completionDate ??
    assignment?.completionDate,
});

const MyPlansTab = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const activeLicenseeLocation = useAtomValue(activeLicenseeLocationAtom);
  const { data } = useAuth();
  const { planDescriptions: planDescriptionsFF } = useFlags();
  const isLicenseeUser = data?.isLicensee;
  const userId = data?.userId;

  const sortOptions = useSortOptions({ myPlans: true });

  const {
    data: plansAssignments,
    isFetching: isFetchingPlansAssignments,
    isPending: isPendingPlansAssignments,
    error: plansAssignmentsError,
  } = useQuery({
    ...getActivePlanAssignmentsOptions(),
  });

  const isFetching = isFetchingPlansAssignments || isPendingPlansAssignments;

  const error = plansAssignmentsError;

  useScrollMemory({
    container: 'body',
    key: DATAVIEW_KEY,
    enabled: !isFetching,
  });

  const [
    {
      display: { items, total },
      filters,
      sort,
    },
    {
      setSort,
      toggleCheckboxFilter,
      setSearch,
      resetAllCheckboxFilters,
      resetCheckboxByValue,
      resetSearch,
    },
  ] = useDataView<MyPlanRow>(DATAVIEW_KEY);

  const currentFilters = Object.values(filters.in).flat();

  const { complianceLabels, noComplianceLabels, statusLabels, allLabels } =
    useCategoryLabels_v2(currentFilters);

  const subnavTabsActive = usePlansShouldShowTabs();

  const filterAndSortButtonOptions: FilterAndSortButtonProps = {
    onSortChange: setSort,
    sortOptions,
    sortValue: sortOptions.find(
      option =>
        option.key === sort?.key && option.direction === sort?.direction,
    ),
    text: `${t('TrainingPlans.filtering.show')} ${
      items.length
    } ${t('TrainingPlans.filtering.results')}`,
  };

  const categories: FilterCheckboxSectionProps = {
    title: t('Browse.categories'),
    labels: isLicenseeUser
      ? [noComplianceLabels]
      : [complianceLabels, noComplianceLabels],
    onToggle: value => toggleCheckboxFilter('category', value),
  };

  const statusOptions: FilterCheckboxSectionProps = {
    title: t('Generic.status'),
    labels: [statusLabels],
    onToggle: value => toggleCheckboxFilter('isCompleted', value),
  };

  const searchOptions: FilterSearchBarProps = {
    onChange: e => {
      setSearch(
        e.target.value,
        planDescriptionsFF
          ? ['nameLocalLang', 'descriptionSearch']
          : 'nameLocalLang',
      );
    },
    onClear: () => resetSearch(),
    placeholder: t('TrainingPlans.filtering.searchPlans'),
    value: filters.search,
  };

  const filterOptions = {
    filterAndSortButtonOptions,
    children: (
      <>
        <Filter.CheckboxSection {...categories} />
        <>
          <Filter.Divider />
          <Filter.CheckboxSection {...statusOptions} />
        </>
      </>
    ),
    search: <Filter.SearchBar {...searchOptions} />,
  };

  // We initially set to the entire plans array and order it
  const coursePlans = useMemo(() => {
    if (Array.isArray(plansAssignments)) {
      const storePlansWithName = plansAssignments.map(userPlan => ({
        ...userPlan,
        planName: itemByLanguage(userPlan.plan?.name ?? {})?.toLowerCase(),
      }));
      return orderBy(storePlansWithName, ['planName'], ['asc']);
    }

    return [];
  }, [plansAssignments]);

  const setSource = useSetAtom(sourceDataFamily<MyPlanRow>('my-plans'));

  useEffect(() => {
    if (coursePlans.length === 0) {
      setSource([]);
      return;
    }

    const filteredStorePlans = isLicenseeUser
      ? coursePlans.filter(
          up => up.plan?.ownerId === activeLicenseeLocation?.number,
        )
      : coursePlans;

    const storePlanItems: MyPlanRow[] = filteredStorePlans
      .filter(
        (
          mp,
        ): mp is typeof mp & {
          plan: NonNullable<typeof mp.plan> & { id: string };
        } => !!mp.plan?.id,
      )
      .map(mp => {
        const allCompleted =
          mp.assignments &&
          mp.assignments.length > 0 &&
          mp.assignments.every(a => a.status === 'COMPLETED');

        return {
          ...addSortFields(mp.plan),
          dueDate: mp.assignments?.at(0)?.dueDate ?? null,
          isCompleted: allCompleted
            ? Constants.PLAN_STATUS.INCLUDE_COMPLETED
            : '',
          assignments: mp.assignments,
        };
      });

    setSource([...storePlanItems]);
  }, [activeLicenseeLocation?.number, coursePlans, isLicenseeUser, setSource]);

  // We check to see if a training plan has been completed to trigger the toast notification
  useEffect(() => {
    const completed = (
      location.state as { completedPlan?: string | boolean } | undefined
    )?.completedPlan;

    if (completed) {
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {`${t('TrainingPlans.toastMessage.completed1')} ${completed}
        
        ${t('TrainingPlans.toastMessage.completed2')}`}
        </ToastMessageBlock>
      ));
      // After we display the toast, we set the state back to false
      navigate(
        {
          pathname: routes.plans.plans,
        },
        {
          replace: true,
          state: { completedPlan: false },
        },
      );
    }
  }, [location.state, navigate, t]);

  if (isApiError(error)) {
    datadogRum.addError(error);
    return <GenericError />;
  }

  return (
    <>
      <LoadingOverlay isOpen={isFetching} fixed />
      <div
        className="relative flex max-w-full grow flex-col"
        data-testid="currentPlansList"
      >
        <>
          {!isFetching && <FilterMobile {...filterOptions} />}
          {/* Top Row */}
          <div className="flex justify-end">
            <div className="w-full md:pl-[304px]">
              <SortFilterHeader
                label={t('TrainingPlans.filtering.sortBy')}
                onChange={setSort}
                onClear={resetAllCheckboxFilters}
                options={sortOptions}
                showClear={false}
                showCompletedPlansOption={true}
                text={
                  total === 1
                    ? `${total} ${t('TrainingPlans.filtering.plan')}`
                    : `${total} ${t('TrainingPlans.filtering.plans')}`
                }
                title={t('TrainingPlans.filtering.myPlans')}
                total={total}
                value={sortOptions.find(
                  option =>
                    option.key === sort?.key &&
                    option.direction === sort?.direction,
                )}
              />
            </div>
          </div>
          {/* Bottom Row */}
          <div className="mt-2.5 flex justify-between">
            {/* Left Side */}
            {!isFetching && (
              <FilterDesktop {...filterOptions} tabsActive={subnavTabsActive} />
            )}
            {/* Right Side */}
            <div className="w-full">
              <div className="relative flex max-w-full grow gap-6">
                <div className="flex max-w-full grow flex-col">
                  <ClearFiltersHeader
                    aliases={allLabels}
                    clearAllFilters={() => {
                      resetAllCheckboxFilters();
                    }}
                    clearFilter={value => {
                      resetCheckboxByValue(value);
                    }}
                    filters={currentFilters}
                  />
                  <div className="relative flex max-w-full grow flex-col">
                    {!items.length &&
                      !isFetching &&
                      !currentFilters.length &&
                      !filters.search && (
                        <NoMessage
                          message={t('TrainingPlans.noTrainingPlans')}
                        />
                      )}
                    {(currentFilters.length || filters.search) && !total && (
                      <NoMessage message={t('TrainingPlans.noPlansResults')} />
                    )}
                    {!!items.length && !isFetching && (
                      <>
                        <div className="flex flex-1 flex-col gap-4">
                          {items
                            .filter(
                              (item): item is MyPlanRow & { id: string } =>
                                !!item.id,
                            )
                            .map(item => (
                              <TeamMemberPlanCard
                                key={item.id}
                                {...toTeamMemberPlanCardProps(
                                  item,
                                  item.assignments?.find(
                                    a => a.userId === userId,
                                  ),
                                )}
                              />
                            ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      </div>
    </>
  );
};

const MyPlansTabWithRoles = withRoles(MyPlansTab, 'plans');

export default MyPlansTabWithRoles;
